/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Card,
  CardHeader,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Box,
  Grid,
  Tooltip,
  LinearProgress
} from '@material-ui/core';
import * as moment from 'moment';
import CakeIcon from '@mui/icons-material/Cake';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CallIcon from '@mui/icons-material/Call';
import MapIcon from '@mui/icons-material/Map';
import BusinessIcon from '@mui/icons-material/Business';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { customerApi } from '../../../api/customerApi';

const InfoItem = ({ icon: Icon, label, value, editing, children, color = "primary.main" }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
    <Box sx={{ 
      p: 1.5, 
      borderRadius: '16px', 
      bgcolor: 'rgba(255,255,255,0.05)', 
      border: '1px solid rgba(255,255,255,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    }}>
      <Icon fontSize="small" />
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        {editing ? children : (
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1b2445', fontSize: '0.95rem' }}>
            {value || '—'}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

const CustomerContactDetails = (props) => {
  const { birthday, civility, firstname, maidenName, lastname, adress, zip, city, email, phone, phoneFix, department, region, notCall, country, birthTown, modeOfExercise, siret, editing,
    autoZip, setAutoZip, errors, handleBlur, handleChange, touched, values, setFieldValue, ...other } = props;
  
  const birthdayToFr = birthday !== null ? moment(birthday).format('DD/MM/YYYY') : 'Non enregistrée';
  const civilityLabel = ['Monsieur', 'Madame', 'Autre'];
  const modeOfExerciseLabel = ['Libéral', 'Salarié en centre de santé', 'Salarié en hôpital', 'Autre'];
  const [localities, setLocalities] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  // 3D Hover Effect
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  function handleMouse(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left - rect.width / 2);
    y.set(event.clientY - rect.top - rect.height / 2);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  const calculateCompleteness = () => {
    const fields = [birthday, adress, zip, city, email, phone];
    const filled = fields.filter(f => f && f !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const completeness = calculateCompleteness();

  const handleZipChange = async (e) => {
    const { value } = e.target;
    handleChange(e);
    if (/^\d{5}$/.test(value)) {
      try {
        const urlPlace = `https://geo.api.gouv.fr/communes?codePostal=${value}&fields=nom,departement,region&format=json&geometry=centre`;
        const response = await fetch(urlPlace);
        const data = await response.json();
        setLocalities(data);
        if (data.length > 0) setAnchorEl(e.target);
      } catch (err) { console.error(err); }
    }
  };

  const handleSelect = (locality) => {
    setFieldValue('city', locality.nom);
    setFieldValue('department', locality.departement.nom);
    setFieldValue('region', locality.region.nom);
    setAnchorEl(null);
  };

  return (
    <Box {...other}>
      <Card className="glass-card" sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
          {/* Top colored bar */}
          <Box sx={{ height: '6px', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }} />
          
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#1b2445', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" /> Identité & Détails
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Complétude {completeness}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={completeness} 
                  sx={{ width: 80, height: 6, borderRadius: 3, mt: 0.5, bgcolor: 'rgba(0,0,0,0.05)', '& .MuiLinearProgress-bar': { borderRadius: 3 } }} 
                />
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ p: 3, borderRadius: '24px', bgcolor: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.05)', mb: 4 }}>
                  <InfoItem icon={VerifiedIcon} label="Civilité & Nom" value={`${civility} ${lastname} ${firstname}`} editing={editing}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Autocomplete
                        options={civilityLabel}
                        size="small"
                        onChange={(e, v) => setFieldValue('civility', v || civility)}
                        value={values.civility}
                        renderInput={(p) => <TextField {...p} label="Civ." variant="outlined" />}
                        sx={{ width: 120 }}
                      />
                      <TextField size="small" name="lastname" value={values.lastname} onChange={handleChange} label="Nom" variant="outlined" />
                      <TextField size="small" name="firstname" value={values.firstname} onChange={handleChange} label="Prénom" variant="outlined" />
                    </Box>
                  </InfoItem>

                  <InfoItem icon={PersonIcon} label="Nom de jeune fille" value={maidenName} editing={editing} color="#ec4899">
                    <TextField size="small" name="maidenName" value={values.maidenName} onChange={handleChange} fullWidth variant="outlined" />
                  </InfoItem>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <InfoItem icon={CakeIcon} label="Naissance" value={birthdayToFr} editing={editing}>
                        <TextField type="date" size="small" name="birthday" value={values.birthday} onChange={handleChange} fullWidth variant="outlined" InputLabelProps={{ shrink: true }} />
                      </InfoItem>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <InfoItem icon={MapIcon} label="Lieu de naissance" value={birthTown} editing={editing} color="#f59e0b">
                        <TextField size="small" name="birthTown" value={values.birthTown} onChange={handleChange} fullWidth variant="outlined" />
                      </InfoItem>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ px: 1, mb: 2, fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Coordonnées
                </Typography>

                <InfoItem icon={PhoneIcon} label="Téléphone portable" value={phone} editing={editing} color="#6366f1">
                  <TextField
                    size="small"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                    fullWidth
                    variant="outlined"
                  />
                </InfoItem>

                <InfoItem icon={EmailIcon} label="Email" value={email} editing={editing} color="#0ea5e9">
                  <TextField
                    size="small"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    fullWidth
                    variant="outlined"
                  />
                </InfoItem>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ px: 1, mb: 2, fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Compléments Professionnels
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <InfoItem icon={BusinessIcon} label="Mode d'exercice" value={modeOfExercise} editing={editing} color="#10b981">
                    <Autocomplete
                      options={modeOfExerciseLabel}
                      size="small"
                      onChange={(e, v) => setFieldValue('modeOfExercise', v || modeOfExercise)}
                      value={values.modeOfExercise}
                      renderInput={(p) => <TextField {...p} label="Mode d'exercice" variant="outlined" />}
                      fullWidth
                    />
                  </InfoItem>
                  
                  <InfoItem icon={CallIcon} label="Téléphone Fixe" value={phoneFix} editing={editing} color="#6366f1">
                    <TextField size="small" name="phoneFix" value={values.phoneFix} onChange={handleChange} fullWidth variant="outlined" />
                  </InfoItem>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 3, opacity: 0.5 }} />
                <Typography variant="subtitle2" sx={{ px: 1, mb: 2, fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Résidence
                </Typography>
                
                <InfoItem icon={LocationOnIcon} label="Adresse Postale" value={adress} editing={editing}>
                  <TextField fullWidth multiline rows={2} name="adress" value={values.adress} onChange={handleChange} variant="outlined" />
                </InfoItem>
                
                <Box sx={{ display: 'flex', gap: 2, ml: 7 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>CP</Typography>
                    {editing ? (
                      <TextField size="small" name="zip" value={values.zip} onChange={handleZipChange} variant="outlined" />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{zip || '—'}</Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Ville</Typography>
                    {editing ? (
                      <TextField size="small" name="city" value={values.city} onChange={handleChange} fullWidth variant="outlined" />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{city || '—'}</Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Pays</Typography>
                    {editing ? (
                      <TextField size="small" name="country" value={values.country} onChange={handleChange} fullWidth variant="outlined" />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{country || '—'}</Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.5 }} />

                <Box>
                  <InfoItem icon={BusinessIcon} label="SIRET" value={siret} editing={editing} color="#0d9488">
                    <TextField
                      size="small"
                      name="siret"
                      value={values.siret}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.siret && errors.siret)}
                      helperText={touched.siret && errors.siret}
                      fullWidth
                      variant="outlined"
                    />
                  </InfoItem>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Decorative corner element */}
          <Box sx={{ 
            position: 'absolute', 
            top: -20, 
            right: -20, 
            width: 60, 
            height: 60, 
            bgcolor: 'rgba(99, 102, 241, 0.05)', 
            borderRadius: '50%',
            zIndex: 0
          }} />
        </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {localities.map((l) => (
          <MenuItem key={l.nom} onClick={() => handleSelect(l)}>
            {l.nom} ({l.departement.nom})
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

CustomerContactDetails.propTypes = {
  birthday: PropTypes.string,
  civility: PropTypes.string,
  lastname: PropTypes.string,
  firstname: PropTypes.string,
  maidenName: PropTypes.string,
  adress: PropTypes.string,
  zip: PropTypes.string,
  city: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  phoneFix: PropTypes.string,
  department: PropTypes.string,
  region: PropTypes.string,
  notCall: PropTypes.string,
  country: PropTypes.string,
  birthTown: PropTypes.string,
  numSecu: PropTypes.string,
  modeOfExercise: PropTypes.string,
  siret: PropTypes.string,
  editing: PropTypes.bool,
  touched: PropTypes.object,
  errors: PropTypes.object,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  setAutoZip: PropTypes.func,
  autoZip: PropTypes.bool
};

export default CustomerContactDetails;
